/**
 * The workspace logo: reading it for display (`useWorkspaceLogoSrc`) and
 * changing it from Settings (`useSaveWorkspaceLogo`, `useRemoveWorkspaceLogo`).
 *
 * The image is fetched through the API client rather than an `<img src>`,
 * because the read route needs the auth and `x-workspace-id` headers an
 * `<img>` cannot send. It is cached as a data URL keyed by the logo's content
 * hash, so it never goes stale: a new logo arrives as a new hash on the
 * workspace payload, and the browser's HTTP cache answers repeat fetches.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api.service';
import {
  getWorkspaceLogo,
  removeWorkspaceLogo,
  uploadWorkspaceLogo,
} from '../../services/api/workspace.api';
import { queryKeys } from '../../lib/queryClient';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { safeAnalytics } from '../../utils/analytics';
import type { WorkspaceLogoBackground } from '@bower/shared/utils/workspaceLogoTile';

const DAY_MS = 24 * 60 * 60 * 1000;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the logo'));
    reader.readAsDataURL(blob);
  });
}

/**
 * The logo as an image `src`, or undefined while it loads, when the workspace
 * has none, or when it failed (the caller then draws the monogram).
 */
export function useWorkspaceLogoSrc(
  workspaceId: string | null | undefined,
  logoHash: string | null | undefined,
): string | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.workspaces.logo(workspaceId ?? '', logoHash ?? ''),
    queryFn: async () =>
      blobToDataUrl(await getWorkspaceLogo(apiService, workspaceId as string, logoHash as string)),
    enabled: !!workspaceId && !!logoHash,
    // Content-addressed: the bytes under one hash never change.
    staleTime: Infinity,
    gcTime: DAY_MS,
    retry: 1,
  });
  return logoHash ? data : undefined;
}

/** The workspace payloads that carry `logoHash`: the switcher list and the detail. */
function refreshWorkspacePayloads(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all, exact: true });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.detail(workspaceId) });
}

export interface SaveWorkspaceLogoInput {
  image: Blob;
  /**
   * The admin cropped, zoomed or rotated before saving: the server keeps
   * their framing instead of trimming the margins.
   */
  edited: boolean;
  /** `transparent` keeps the see-through parts see-through; `auto` lets the server pick a tile. */
  background: WorkspaceLogoBackground;
  /** The picked file's type, for analytics (a closed set). */
  sourceType: string;
  /** Reopened from the current logo rather than a newly picked file. */
  fromCurrent: boolean;
}

export function useSaveWorkspaceLogo(workspaceId: string, hadLogo: boolean) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ image, edited, background }: SaveWorkspaceLogoInput) => {
      const res = await uploadWorkspaceLogo(apiService, workspaceId, image, {
        keepFraming: edited,
        background,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Could not save the logo');
      }
      return res.data;
    },
    onSuccess: (_data, { edited, background, sourceType, fromCurrent }) => {
      refreshWorkspacePayloads(queryClient, workspaceId);
      safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_LOGO_UPLOADED, {
        was_replaced: hadLogo,
        edited,
        background,
        source_type: sourceType,
        from_current: fromCurrent,
      });
    },
  });
}

export function useRemoveWorkspaceLogo(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await removeWorkspaceLogo(apiService, workspaceId);
      if (!res.success) throw new Error(res.error?.message || 'Could not remove the logo');
    },
    onSuccess: () => {
      refreshWorkspacePayloads(queryClient, workspaceId);
      safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_LOGO_REMOVED);
    },
  });
}
